import { useEffect, useRef, useState, type DragEvent } from 'react';
import { adminApi, type MediaItem } from '../../lib/adminApi';

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (url: string) => void;
  dogId?: string;
};

export function MediaModal({ open, onClose, onSelect, dogId }: Props) {
  const [tab, setTab] = useState<'library' | 'upload'>('library');
  const [items, setItems] = useState<MediaItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState('');
  const [uploading, setUploading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const onCloseRef = useRef(onClose);

  useEffect(() => { onCloseRef.current = onClose; }, [onClose]);

  useEffect(() => {
    if (!open) return;
    setSelected(''); setError(''); setTab('library'); setLoading(true);
    adminApi.listMedia().then(setItems).catch(() => setError('Could not load your library.')).finally(() => setLoading(false));
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const previouslyFocused = document.activeElement as HTMLElement | null;
    const focusTimer = window.setTimeout(() => closeRef.current?.focus(), 0);
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onCloseRef.current();
        return;
      }
      if (event.key !== 'Tab') return;
      const focusable = Array.from(dialogRef.current?.querySelectorAll<HTMLElement>('button:not(:disabled), input:not(:disabled), [href], [tabindex]:not([tabindex="-1"])') ?? []);
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    };
    document.addEventListener('keydown', onKeyDown);
    return () => {
      window.clearTimeout(focusTimer);
      document.removeEventListener('keydown', onKeyDown);
      previouslyFocused?.focus();
    };
  }, [open]);

  if (!open) return null;

  const doUpload = async (file: File) => {
    if (!file.type.startsWith('image/')) { setError('Choose a PNG, JPG, or WEBP image.'); return; }
    if (file.size > 10 * 1024 * 1024) { setError('Images must be 10 MB or smaller.'); return; }
    setUploading(true); setError('');
    try {
      const { deliveryURL } = await adminApi.uploadMedia(file, dogId ? { dogId } : {});
      setItems(await adminApi.listMedia().catch(() => items));
      setSelected(deliveryURL);
      setTab('library');
    } catch {
      setError('Upload failed — check R2 is configured.');
    } finally {
      setUploading(false);
    }
  };

  const onDrop = (event: DragEvent) => {
    event.preventDefault(); setDragging(false);
    const file = event.dataTransfer.files?.[0];
    if (file) doUpload(file);
  };

  return (
    <div className="ed-modal-backdrop" onClick={onClose}>
      <div ref={dialogRef} className="ed-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true" aria-labelledby="ed-media-title">
        <header className="ed-modal-head">
          <h2 id="ed-media-title">Select image</h2>
          <button ref={closeRef} type="button" className="ed-modal-x" onClick={onClose} aria-label="Close">✕</button>
        </header>

        <div className="ed-modal-tabs" role="tablist" aria-label="Image source">
          <button type="button" role="tab" aria-selected={tab === 'library'} className={tab === 'library' ? 'on' : ''} onClick={() => setTab('library')}>Library</button>
          <button type="button" role="tab" aria-selected={tab === 'upload'} className={tab === 'upload' ? 'on' : ''} onClick={() => setTab('upload')}>Upload</button>
        </div>

        <div className="ed-modal-body" role="tabpanel" aria-label={tab === 'library' ? 'Image library' : 'Upload image'}>
          {tab === 'library' ? (
            loading ? <p className="ed-modal-empty">Loading your library…</p>
              : items.length === 0 ? <p className="ed-modal-empty">No images yet. Switch to <b>Upload</b> to add your first one.</p>
                : (
                  <div className="ed-media-grid">
                    {items.map((item) => (
                      <button key={item.key} type="button" className={`ed-media-cell ${selected === item.url ? 'sel' : ''}`} aria-label={`Select ${item.key.split('/').pop() || 'image'}`} aria-pressed={selected === item.url} onClick={() => setSelected(item.url)}>
                        <img src={item.url} alt="" loading="lazy" />
                        {selected === item.url && <span className="ed-media-check">✓</span>}
                      </button>
                    ))}
                  </div>
                )
          ) : (
            <div
              className={`ed-drop ${dragging ? 'drag' : ''}`}
              onClick={() => !uploading && fileRef.current?.click()}
              onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={onDrop}
            >
              <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => e.target.files?.[0] && doUpload(e.target.files[0])} />
              <div className="ed-drop-inner">
                <div className="ed-drop-icon">⬆</div>
                <p>{uploading ? 'Uploading…' : 'Drop an image here, or click to browse'}</p>
                <span>PNG, JPG, WEBP · up to 10 MB</span>
              </div>
            </div>
          )}
          {error && <p className="ed-modal-error" role="alert">{error}</p>}
        </div>

        <footer className="ed-modal-foot">
          <button type="button" className="ed-btn" onClick={onClose}>Cancel</button>
          <button type="button" className="ed-btn ed-btn-primary" disabled={!selected} onClick={() => { onSelect(selected); onClose(); }}>Select</button>
        </footer>
      </div>
    </div>
  );
}
